import { useEffect, useRef, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import {
  ApiError,
  listCommsMessages,
  sendCommsMessage,
  type CommsMessage,
} from '../lib/api';
import type { ChatLane } from '../routes/route';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import { onServiceWorkerPushMessage } from '../lib/swMessages';
import './ChatThread.css';

// CV2 — ONE THREAD, TWO PEOPLE. The office's Conversations pane and the
// tech's Chat tab render the same wire (§7 of the CV2 spec) and diverge only
// in which side of it is "me", so the thread is one component with a
// `viewerKind` — never two implementations that could disagree about what a
// message looks like.
//
// NEWEST-FIRST (CV1's keyset mode). The first page is the latest N
// (`order=desc`), scroll-back pages by `before=<oldest loaded id>`
// (exclusive), and an empty page means history is exhausted. This moved the
// OFFICE thread to newest-first too (it paged oldest-first via offset in
// S5b) — deliberately, per the spec's own leave: a chat opened on its
// oldest page is a chat opened on the wrong end.
//
// ATTRIBUTION IS `senderKind`, NEVER PARSED. Stored bodies are raw — the
// "Marco: " prefix a Telegram room sees is that door's rendering only
// (CV1 §3) — so nothing here ever splits a body on a colon.
//
// CHAT DOES NOT QUEUE OFFLINE, by decision: capture and chat are distinct
// instruments. Offline, the composer disables with the field banner's own
// calm vocabulary (`◌ No signal` — no red, no apology, no outbox), and a
// send that fails mid-flight says so and KEEPS THE DRAFT.

export interface ChatThreadProps {
  tenantId: string;
  lane: ChatLane;
  /** The work order id (job lane) or membership id (DM lanes). */
  id: string;
  /** Which senderKind is "me" — decides bubble sides, nothing else. */
  viewerKind: 'office' | 'tech';
  /** A standing fact about the thread (topic pending, enrolment pending),
   * rendered above the messages. */
  notice?: string | null;
  /** Quiet doors rendered between the messages and the composer (the
   * tech's Open-in-Telegram line, the office's People panel). */
  extra?: ComponentChildren;
  /** The server's stored row after a 201 — so a list can update itself. */
  onSent?: (message: CommsMessage) => void;
}

type LoadState = 'loading' | 'ready' | 'error';

/** One page of a thread, newest first. The page BEFORE this one is a
 * button, never an automatic sweep (Core register gap 11). */
const PAGE = 100;

function clockOf(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export function ChatThread({ tenantId, lane, id, viewerKind, notice, extra, onSent }: ChatThreadProps) {
  /** Ascending for display; the wire pages are descending and get reversed
   * at the boundary, once. */
  const [messages, setMessages] = useState<CommsMessage[]>([]);
  const [load, setLoad] = useState<LoadState>('loading');
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  /** True once a page came back short or empty — history exhausted. */
  const [exhausted, setExhausted] = useState(false);
  const [paging, setPaging] = useState(false);
  const [nonce, setNonce] = useState(0);

  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  /** The send's own account of itself — the 422 names the missing step and
   * is rendered verbatim, never flattened into "could not send". */
  const [sendError, setSendError] = useState<string | null>(null);

  /** The field banner's fact, tracked live so the composer can stand down
   * calmly instead of promising a send that cannot leave. */
  const [online, setOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine !== false
  );

  const scroller = useRef<HTMLDivElement>(null);
  const threadKey = `${lane}:${id}`;

  const wire = (page: { limit: number; before?: string }) =>
    listCommsMessages(tenantId, {
      lane,
      ...(lane === 'job' ? { recordId: id } : { membershipId: id }),
      order: 'desc',
      ...page,
    });

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  /* CV6b §1 — THE PUSH THAT DELIBERATELY DID NOT BUZZ.
   *
   * sw-push.js answers 'update-only' when the thread on screen is visible
   * and focused: no notification, because the human is looking straight at
   * it. That decision is only honest if the thread then CATCHES UP — the
   * alternative is an app that silently falls behind the conversation
   * someone is reading, which is worse than a redundant buzz.
   *
   * The listener lives here rather than in the shell because this component
   * is the only thing that knows which thread it is showing, and the worker
   * addresses its message by route. */
  useEffect(
    () =>
      onServiceWorkerPushMessage((message) => {
        if (message.verb !== 'refresh') return;
        const mine = `/chat/${lane}/${id}`;
        if (message.path !== mine) return;
        setNonce((n) => n + 1);
      }),
    [lane, id]
  );

  useEffect(() => {
    let cancelled = false;
    setLoad('loading');
    setFailure(null);
    setSendError(null);
    wire({ limit: PAGE })
      .then((page) => {
        if (cancelled) return;
        setMessages([...page].reverse());
        setExhausted(page.length < PAGE);
        setLoad('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setMessages([]);
        setFailure(describeReadFailure(err));
        setLoad('error');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, threadKey, nonce]);

  // Newest in view on arrival and on send — never on a scroll-back prepend
  // (loadEarlier keeps its own anchor). Instant, not smooth: a jump to the
  // present is not an animation, and reduced-motion needs nothing special.
  const bottomCount = useRef(0);
  useEffect(() => {
    const el = scroller.current;
    if (el && messages.length !== bottomCount.current) {
      el.scrollTop = el.scrollHeight;
    }
    bottomCount.current = messages.length;
  }, [messages.length === 0 ? 0 : messages[messages.length - 1]?.id, threadKey, load]);

  async function loadEarlier() {
    const oldest = messages[0];
    if (paging || !oldest) return;
    setPaging(true);
    try {
      const page = await wire({ limit: PAGE, before: oldest.id });
      if (page.length === 0) {
        setExhausted(true);
      } else {
        const el = scroller.current;
        const keep = el ? el.scrollHeight - el.scrollTop : 0;
        setMessages((prev) => [...[...page].reverse(), ...prev]);
        setExhausted(page.length < PAGE);
        // Keep the reader's place: the same messages stay in view while
        // history grows above them.
        requestAnimationFrame(() => {
          const now = scroller.current;
          if (now) now.scrollTop = now.scrollHeight - keep;
        });
      }
    } catch (err) {
      setFailure(describeReadFailure(err));
    }
    setPaging(false);
  }

  async function send() {
    const body = draft.trim();
    if (!body || sending || !online) return;
    setSending(true);
    setSendError(null);
    try {
      const message = await sendCommsMessage(tenantId, {
        lane,
        ...(lane === 'job' ? { recordId: id } : { membershipId: id }),
        body,
      });
      // SEND-THEN-STORE: the bubble is the server's own stored row, never a
      // hopeful local echo.
      setMessages((prev) => [...prev, message]);
      setDraft('');
      onSent?.(message);
    } catch (err) {
      // The server's own sentence, kept whole — and the draft kept with it.
      setSendError(
        err instanceof ApiError
          ? err.message
          : 'That did not reach the server, so it did not reach anyone. Nothing was stored — your words are still below.'
      );
    }
    setSending(false);
  }

  return (
    <div class="cthread">
      {notice ? (
        <div class="cthread__notice" role="status">
          {notice}
        </div>
      ) : null}

      <div class="cthread__scroll" ref={scroller}>
        {load === 'loading' ? (
          <div class="cthread__empty">One moment…</div>
        ) : failure !== null ? (
          // A refused lane reads as "not yours", never as an empty
          // conversation (the 2026-08-11 lesson).
          <div class="cthread__say" data-testid="thread-failure">
            {failure.headline}
            <br />
            <span class="cthread__soft">{failure.detail}</span>
            {failure.retryable ? (
              <>
                <br />
                <button type="button" class="cthread__more" onClick={() => setNonce((n) => n + 1)}>
                  Read it again
                </button>
              </>
            ) : null}
          </div>
        ) : messages.length === 0 ? (
          // Empty and FINE — a quiet lane is the ordinary state and must not
          // wear a refused read's face.
          <div class="cthread__empty" data-testid="thread-empty">
            Nothing has been said here yet.
          </div>
        ) : (
          <>
            {!exhausted ? (
              <button
                type="button"
                class="cthread__more"
                disabled={paging}
                onClick={() => void loadEarlier()}
              >
                {paging ? 'One moment…' : 'Earlier messages'}
              </button>
            ) : null}
            {messages.map((m) =>
              m.senderKind === 'system' ? (
                // Rexx posting what it did — the record speaking, never a
                // person's bubble.
                <div class="cthread__msg cthread__msg--sys" key={m.id} data-testid="thread-system">
                  <b>Rexx</b> · {m.body}
                </div>
              ) : (
                <div
                  class={`cthread__msg cthread__msg--${m.senderKind === viewerKind ? 'me' : 'them'}`}
                  key={m.id}
                >
                  {m.senderKind !== viewerKind ? (
                    <span class="cthread__who">{m.senderKind === 'office' ? 'Office' : 'Tech'}</span>
                  ) : null}
                  {m.body}
                  {m.metadata?.mediaKind ? (
                    <span class="cthread__media">{m.metadata.mediaKind} — held in Telegram</span>
                  ) : null}
                  <span class="cthread__t">{clockOf(m.createdAt)}</span>
                </div>
              )
            )}
          </>
        )}
      </div>

      {extra}

      {sendError !== null ? (
        <div class="cthread__senderr" role="status" data-testid="thread-send-error">
          {sendError}
        </div>
      ) : null}

      {!online ? (
        // The field banner's own vocabulary: a fact, not an incident. Chat
        // deliberately has no outbox — capture queues, conversation waits.
        <div class="cthread__offline" role="status" data-testid="thread-offline">
          <span aria-hidden="true">◌</span> No signal · chat waits for it — what you typed stays put
        </div>
      ) : null}

      <div class="cthread__composer">
        <input
          class="cthread__box"
          type="text"
          aria-label="Message"
          placeholder="Message…"
          value={draft}
          disabled={!online}
          onInput={(e) => setDraft((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void send();
          }}
        />
        <button
          type="button"
          class="cthread__send"
          disabled={!online || sending || draft.trim().length === 0}
          onClick={() => void send()}
        >
          {sending ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  );
}
